import { useEffect, useState } from "react";
import { GlobalTable } from "../../../global/components/GlobalTable";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Col, Input, Row, Select } from "antd";
import { PlatesColumns } from "./Columns";
import ConfirmModal from "../../../global/components/ConfirmModal";
import useDelete from "../../../global/hooks/useDelete";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import { HiArrowPath } from "react-icons/hi2";
import useGet from "../../../global/hooks/useGet";
import { getModifiedResponse } from "../../../global/utils/responseModifier";
import {
  getDealerNo,
  getOemNo,
  hasRole,
} from "../../../global/hooks/getUserRole";

const initialFilterState = {
  PlateName: "",
  PlateCode: "",
  OemNo: null,
  DealerNo: "",
};

export const Plates = () => {
  const [hoveredRow, setHoveredRow] = useState(null);
  const [listData, setListData] = useState([]);
  const [params, setParams] = useSearchParams({
    limit: 10,
    sort_by: "PlateNo",
    order: "desc",
  });
  const modParams = { ...Object.fromEntries([...params?.entries()]) } || {};
  /** Dealer accounts (roles 7, 8): scoped to own dealer; OEM/Dealer filters hidden; DealerNo sent on API only. */
  const isDealerAccount = hasRole(7, 8);
  const isOemAccount = hasRole(4, 5, 6);
  const initialPagination = {
    current: parseInt(modParams.page) || 1,
    pageSize: parseInt(modParams.limit) || 10,
    total: 0,
  };
  const [pagination, setPagination] = useState(initialPagination);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [filter, setFilter] = useState(initialFilterState);
  const [showModal, setShowModal] = useState(false);

  const navigate = useNavigate();

  const query = new URLSearchParams(params);
  query.delete("pageSize");
  query.delete("total");
  query.delete("current");
  if (isDealerAccount) {
    const dn = getDealerNo();
    if (dn != null && dn !== "") {
      query.set("DealerNo", String(dn));
    }
  }
  if (isOemAccount) {
    const on = getOemNo();
    if (on != null && on !== "") {
      query.set("OemNo", String(on));
    }
  }
  const updatedQuery = query.toString();
  const url = updatedQuery
    ? `${process.env.REACT_APP_API_URL}/api/plates/?${updatedQuery}`
    : `${process.env.REACT_APP_API_URL}/api/plates/`;
  const { data, loading, refetch } = useGet(url);
  const optionUrl = `${process.env.REACT_APP_API_URL}/api/accounts/options/`;
  const { data: optionData } = useGet(optionUrl);

  const dealerOptionsOemNo = isOemAccount ? getOemNo() : filter?.OemNo;
  const dealerOptionsUrl =
    !isDealerAccount && dealerOptionsOemNo
      ? `${process.env.REACT_APP_API_URL}/api/dealers/oems/${dealerOptionsOemNo}/dealers/`
      : null;
  const { data: dealerOptions } = useGet(dealerOptionsUrl);

  const modifiedOems = getModifiedResponse(optionData, "oems");
  const modifiedDealers = getModifiedResponse(dealerOptions, null);

  let deleteUrl;
  selectedRowKeys.length > 1
    ? (deleteUrl = `${process.env.REACT_APP_API_URL}/api/plates/bulk-delete/`)
    : (deleteUrl = `${process.env.REACT_APP_API_URL}/api/plates/${selectedRowKeys}/`);

  const {
    data: deletedData,
    error: deletedDataError,
    deleteData,
    deleteMultiple,
  } = useDelete(deleteUrl);

  useEffect(() => {
    const matchedFilter = {};
    for (const key in initialFilterState) {
      if (modParams[key] !== undefined) {
        matchedFilter[key] = modParams[key];
      }
    }

    setFilter((prev) => ({
      ...prev,
      ...matchedFilter,
    }));
  }, []);

  const paginationConfig = {
    current: pagination.current,
    pageSize: pagination.pageSize,
    total: data?.count,
    showSizeChanger: true,
    pageSizeOptions: [10, 20, 50, 100],
    showTotal: (total, range) => `Total ${total} items ${range[0]}-${range[1]}`,
    onChange: (page, pageSize) => {
      setPagination({
        ...pagination,
        current: page,
        pageSize: pageSize,
      });

      const updatedParams = {
        ...Object.fromEntries([...params.entries()]),
        limit: pageSize,
        page: page,
      };
      setParams(updatedParams);
    },
  };

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    fixed: true,
  };

  const onRow = (record, rowIndex) => {
    return {
      onClick: (event) => {
        navigate(`/virtual-qr/info/${record?.PlateNo}`);
      },
    };
  };

  const handleFilterChange = (e) => {
    const inputName = e.target.name;
    const inputValue = e.target.value.replace(/['""`''""]/g, "");
    setFilter({ ...filter, [inputName]: inputValue });
  };
  const onSearch = (value) => {
    console.log("search:", value);
  };

  const handleFilter = () => {
    const filterDict = {};
    for (const key in filter) {
      if (
        filter[key] !== null &&
        filter[key] !== "" &&
        filter[key] !== undefined
      ) {
        filterDict[key] = filter[key];
      }
    }
    const isEmpty = JSON.stringify(filterDict) === "{}";

    if (isEmpty) {
      handleReset();
    } else {
      const params = {
        limit: 10,
        sort_by: "PlateNo",
        order: "desc",
        page: 1,
        ...filterDict,
      };
      setPagination((prev) => ({ ...prev, current: 1 }));
      setParams(params);
      refetch();
    }
  };

  const handleReset = () => {
    setFilter({ ...initialFilterState });
    setPagination({
      current: 1,
      pageSize: 10,
      total: 0,
    });
    const params = {
      limit: 10,
      sort_by: "PlateNo",
      order: "desc",
    };
    setParams(params);
    refetch();
  };

  const onDeleteConfirm = async () => {
    try {
      if (selectedRowKeys.length === 1) {
        await deleteData();
      } else {
        await deleteMultiple({ ids: selectedRowKeys });
      }
    } catch (error) {
      openErrorNotification("Something went wrong.");
    }
    setShowModal(false);
    setSelectedRowKeys([]);
  };

  useEffect(() => {
    if (deletedData && !deletedDataError) {
      openConfirmNotification("Success");
      refetch();
    } else if (deletedDataError) {
      openErrorNotification(
        deletedDataError?.response?.data?.message || "Something went wrong",
      );
    }
  }, [deletedData, deletedDataError]);

  useEffect(() => {
    if (data && !loading) {
      const newData = data?.data?.map((el) => ({
        ...el,
        key: el?.PlateNo,
      }));
      setListData(newData);
    }
  }, [data, loading]);

  const breadcrumbItems = [
    {
      label: "Virtual QR/NFC",
      href: "/virtual-qr",
    },
  ];

  return (
    <div>
      <div className="pb-4 -mt-4 pt-[6px]">
        <CommonBreadcrumb breadcrumbItems={breadcrumbItems} />
      </div>
      <div className="p-4 bg-white rounded-[4px] border border-gray-200">
        <div className="mb-4">
          <div className="mt-2">
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={24} md={12} lg={8} xl={6} className="!h-[32px]">
                <Input
                  className="input_text_3 mr10 h-full"
                  placeholder="Name"
                  value={filter.PlateName}
                  onChange={handleFilterChange}
                  name="PlateName"
                />
              </Col>
              <Col xs={24} sm={24} md={12} lg={8} xl={6} className="!h-[32px]">
                <Input
                  className="input_text_3 mr10 h-full"
                  placeholder="Code"
                  value={filter.PlateCode}
                  onChange={handleFilterChange}
                  name="PlateCode"
                />
              </Col>
              {!isDealerAccount ? (
                <>
                  {!isOemAccount && (
                  <Col xs={24} sm={24} md={12} lg={8} xl={6} className="!h-[32px]">
                    <Select
                      showSearch
                      placeholder="OEM"
                      optionFilterProp="label"
                      value={
                        filter.OemNo || filter.OemNo === 0
                          ? Number(filter.OemNo)
                          : undefined
                      }
                      onChange={(value) =>
                        setFilter((prev) => ({
                          ...prev,
                          OemNo: value ?? null,
                          DealerNo: "",
                        }))
                      }
                      onSearch={onSearch}
                      options={modifiedOems}
                      style={{ width: "100%" }}
                      allowClear
                    />
                  </Col>
                  )}
                  <Col
                    xs={24}
                    sm={24}
                    md={12}
                    lg={8}
                    xl={6}
                    className="!h-[32px]"
                  >
                    <Select
                      showSearch
                      placeholder="Dealer"
                      optionFilterProp="label"
                      value={
                        filter.DealerNo
                          ? Number(filter.DealerNo)
                          : undefined
                      }
                      onChange={(value) =>
                        setFilter((prev) => ({
                          ...prev,
                          DealerNo: value ?? "",
                        }))
                      }
                      onSearch={onSearch}
                      options={modifiedDealers}
                      style={{ width: "100%" }}
                      allowClear
                      disabled={
                        !isOemAccount &&
                        (filter.OemNo === undefined ||
                          filter.OemNo === null ||
                          filter.OemNo === "")
                      }
                    />
                  </Col>
                </>
              ) : null}
            </Row>
          </div>

          <div className="w-full overflow-x-auto">
            <div className="flex justify-between my-4 flex-nowrap min-w-[610px]">
              <div className="flex items-center">
                <Button
                  type="primary"
                  className="mr-2 w-[104px] h-[30px] font-medium"
                  onClick={handleFilter}
                >
                  <IoIosCheckmarkCircleOutline className="!text-[14px] font-medium" />
                  Apply
                </Button>
                <Button className="reset-button" onClick={handleReset}>
                  <HiArrowPath className="!text-[14px] font-medium" />
                  Reset
                </Button>
                <Button
                  type="primary"
                  className="mr-2 -ml-2 w-[104px] h-[30px] font-medium"
                  onClick={() => navigate("/virtual-qr/add")}
                >
                  <PlusOutlined />
                  Add
                </Button>
                <Button
                  type="primary"
                  disabled={selectedRowKeys?.length === 0}
                  className="mr-2 w-[104px] h-[30px] font-medium"
                  onClick={() => {
                    setShowModal(true);
                  }}
                >
                  <DeleteOutlined />
                  Delete
                </Button>
              </div>
            </div>
          </div>
          <ConfirmModal
            showModal={showModal}
            setShowModal={setShowModal}
            onDeleteConfirm={onDeleteConfirm}
            setSelectedRowKeys={setSelectedRowKeys}
          />
          <GlobalTable
            columns={PlatesColumns(
              hoveredRow,
              setShowModal,
              setSelectedRowKeys,
              setParams,
              params,
              isDealerAccount,
            )}
            dataSource={listData}
            rowSelection={rowSelection}
            pagination={paginationConfig}
            onRow={(record, rowIndex) => {
              return {
                onMouseEnter: () => setHoveredRow(rowIndex),
                onMouseLeave: () => setHoveredRow(null),
                ...onRow?.(record, rowIndex),
              };
            }}
            loading={loading}
          />
        </div>
      </div>
    </div>
  );
};
